import {
  ChangeEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as R from 'remeda';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { BehaviorSubject } from 'rxjs';
import { IconX, IconZoomReplace } from '@rossum/ui/icons/tabler';
import {
  Button,
  FormControlLabel,
  Stack,
  SvgIcon,
  Switch,
  TextField,
  Typography,
} from '@rossum/ui/material';
import { isFieldEditableFormula } from '../../../components/Datapoint/helpers';
import { findAll } from '../../../lib/searchAndReplaceUtils';
import {
  bulkUpdateDatapointValue,
  rerenderDatapoints,
} from '../../../redux/modules/datapoints/actions';
import { schemaMapSelector } from '../../../redux/modules/schema/schemaMapSelector';
import { getFooterSchema } from '../../../redux/modules/schema/selectors';
import { setCurrentFooterColumn } from '../../../redux/modules/searchAndReplace/actions';
import { getDatapointsBySchemaId } from '../../../redux/modules/searchAndReplace/selectors';
import styles from '../style.module.sass';

type SearchAndReplaceProps = {
  currentFooterColumn: string;
};

export const searchInputSubject = new BehaviorSubject<string>('');
export const replaceInputSubject = new BehaviorSubject<string>('');
export const togglePreviewSubject = new BehaviorSubject<boolean>(false);

const SearchAndReplace = ({ currentFooterColumn }: SearchAndReplaceProps) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const schemaMap = useSelector(schemaMapSelector);
  const columnSchema = schemaMap.get(currentFooterColumn);

  const currentColumnDatapoints = useSelector(getDatapointsBySchemaId);

  const [search, setSearch] = useState<string>('');
  const [replace, setReplace] = useState<string>('');
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [isSearchAndReplaceSubmitted, setIsSearchAndReplaceSubmitted] =
    useState<boolean>(false);
  const [numberOfItems, setNumberOfItems] = useState<number>(0);
  const [numberOfReplacedItems, setNumberOfReplacedItems] = useState<number>(0);

  const inputSearch = useRef<HTMLInputElement>(null);

  const escapedValue = (value: string) => value.replace(/\n/g, ' ');

  useEffect(() => {
    inputSearch.current?.focus();
  }, [currentFooterColumn]);

  useEffect(() => {
    const currentChunks = R.flat(
      currentColumnDatapoints.map(({ content }) =>
        findAll({
          autoEscape: true,
          caseSensitive: true,
          searchWords: [search],
          textToHighlight: content?.value ? escapedValue(content.value) : '',
        })
      ),
      10
    );
    setNumberOfItems(
      R.countBy(currentChunks, chunk => (chunk.highlight ? 'true' : 'false'))
        .true || 0
    );
  }, [search, currentColumnDatapoints]);

  const onSearchChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      if (isSearchAndReplaceSubmitted) {
        setIsSearchAndReplaceSubmitted(false);
      }
      setSearch(value);
      searchInputSubject.next(value);
    },
    [isSearchAndReplaceSubmitted]
  );

  const onReplaceChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      if (isSearchAndReplaceSubmitted) {
        setIsSearchAndReplaceSubmitted(false);
      }
      setReplace(value);
      replaceInputSubject.next(value);
    },
    [isSearchAndReplaceSubmitted]
  );

  const onToggle = useCallback(() => {
    if (isSearchAndReplaceSubmitted) {
      setIsSearchAndReplaceSubmitted(false);
    }
    setShowPreview(!showPreview);
    togglePreviewSubject.next(!showPreview);
  }, [isSearchAndReplaceSubmitted, showPreview]);

  const onReplaceAll = useCallback(() => {
    const noRecalculation = isFieldEditableFormula(columnSchema);

    const datapointsToUpdate = currentColumnDatapoints.reduce<
      Record<
        number,
        { newValue: string; noRecalculation?: boolean | undefined }
      >
    >((updates, dp) => {
      const oldValue = dp.content?.value ? escapedValue(dp.content.value) : '';
      const newValue = oldValue.replaceAll(search, replace);

      return oldValue.includes(search)
        ? {
            ...updates,
            [dp.id]: {
              newValue,
              ...(noRecalculation === true ? { noRecalculation } : {}),
            },
          }
        : updates;
    }, {});

    dispatch(bulkUpdateDatapointValue(datapointsToUpdate));
  }, [columnSchema, currentColumnDatapoints, dispatch, replace, search]);

  const onSubmit = useCallback(() => {
    setIsSearchAndReplaceSubmitted(true);
    setNumberOfReplacedItems(numberOfItems);
    onReplaceAll();
    dispatch(rerenderDatapoints());
  }, [dispatch, numberOfItems, onReplaceAll]);

  const closePortal = useCallback(() => {
    setSearch('');
    setReplace('');
    searchInputSubject.next('');
    replaceInputSubject.next('');
    togglePreviewSubject.next(false);
    return dispatch(setCurrentFooterColumn(''));
  }, [dispatch]);

  const { columns } = useSelector(getFooterSchema);

  const label = useMemo(
    () =>
      columns.find(c => c.id === currentFooterColumn)?.label ??
      currentFooterColumn,
    [columns, currentFooterColumn]
  );

  return (
    <>
      <div className={styles['SearchAndReplaceHeader']}>
        <Stack direction="row" alignItems="center" px={2} py={1} spacing={0.75}>
          <SvgIcon fontSize="medium">
            <IconZoomReplace />
          </SvgIcon>
          <Typography variant="body2">
            <FormattedMessage
              id="components.documentValidation.footer.searchAndReplace.replaceInColumn"
              values={{ column: <strong>{label}</strong> }}
            />
          </Typography>
        </Stack>
        <div onClick={closePortal} className={styles['DropdownClose']}>
          <SvgIcon fontSize="small">
            <IconX />
          </SvgIcon>
        </div>
      </div>
      <div className={styles['SearchAndReplaceInputWrapper']}>
        <TextField
          value={search}
          size="small"
          inputRef={inputSearch}
          focused={!!search}
          onChange={onSearchChange}
          placeholder={intl.formatMessage({
            id: 'components.documentValidation.footer.searchAndReplace.find',
          })}
        />
      </div>
      <div className={styles['SearchAndReplaceInputWrapper']}>
        <TextField
          value={replace}
          focused={!!replace}
          onChange={onReplaceChange}
          placeholder={intl.formatMessage({
            id: 'components.documentValidation.footer.searchAndReplace.replace',
          })}
          size="small"
        />
      </div>
      <div className={styles['SearchAndReplaceMessage']}>
        {isSearchAndReplaceSubmitted ? (
          <FormattedMessage
            id="components.documentValidation.footer.searchAndReplace.replaceDone"
            values={{
              numberOfReplacedItems,
              search,
              replace,
              italic: (msg: ReactNode) => <i>{msg}</i>,
              roboto: (msg: ReactNode) => (
                <span style={{ fontWeight: 700 }}>{msg}</span>
              ),
            }}
          />
        ) : (
          <FormattedMessage
            id="components.documentValidation.footer.searchAndReplace.numberOfItems"
            values={{ numberOfItems }}
          />
        )}
      </div>
      <div className={styles['SearchAndReplaceAction']}>
        <FormControlLabel
          control={<Switch checked={showPreview} onChange={onToggle} />}
          labelPlacement="end"
          label={
            <Typography variant="body2">
              {intl.formatMessage({
                id: 'components.documentValidation.footer.searchAndReplace.preview',
              })}
            </Typography>
          }
        />

        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={!search || !numberOfItems || isSearchAndReplaceSubmitted}
        >
          <FormattedMessage id="components.documentValidation.footer.searchAndReplace.buttonReplace" />
        </Button>
      </div>
    </>
  );
};

export default SearchAndReplace;
